<template>
  <section class="flex flex-col gap-4">
    <div class="flex items-center justify-between gap-3">
      <p class="text-sm text-muted-foreground">
        {{ itemCountLabel }}
      </p>
      <p class="text-xs text-muted-foreground">
        {{ isEditable ? 'Ordered by template sequence' : 'Read-only version' }}
      </p>
    </div>

    <div v-if="isEditable && sortedChecklistItemTemplates.length > 0" class="flex flex-wrap items-center gap-2 rounded-md border bg-muted/20 p-2">
      <p class="text-xs text-muted-foreground">
        {{ selectedItemCountLabel }}
      </p>
      <Button size="sm" variant="outline" :disabled="isBulkActionBusy" @click="toggleSelectAllChecklistItemTemplates">
        {{ areAllChecklistItemTemplatesSelected ? 'Clear Selection' : 'Select All' }}
      </Button>
      <Button
        size="sm"
        variant="outline"
        class="text-destructive"
        :loading="isBulkDeletingChecklistItemTemplates"
        :disabled="!canDeleteSelectedChecklistItemTemplates"
        @click="deleteSelectedChecklistItemTemplates"
      >
        Delete Selected
      </Button>
    </div>

    <p v-if="errorMessage" class="text-sm text-destructive">
      {{ errorMessage }}
    </p>

    <p v-if="reorderErrorMessage" class="text-sm text-destructive">
      {{ reorderErrorMessage }}
    </p>

    <div v-if="checklistItemTemplatesStore.isLoading && sortedChecklistItemTemplates.length === 0" class="rounded-md border border-dashed p-4 text-sm text-muted-foreground">
      Loading checklist template items...
    </div>

    <p v-else-if="!checklistItemTemplatesStore.isLoading && sortedChecklistItemTemplates.length === 0" class="rounded-md border border-dashed p-4 text-sm text-muted-foreground">
      No checklist template items were found for this checklist template.
    </p>

    <VueDraggable
      v-else-if="isEditable"
      v-model="editableSortedChecklistItemTemplates"
      tag="ul"
      class="flex flex-col gap-3"
      handle=".checklist-template-item-drag-handle"
      :animation="150"
      :disabled="isReorderingChecklistItemTemplates"
      @end="handleChecklistItemTemplateSortEnd"
    >
      <template v-for="itemTemplate in editableSortedChecklistItemTemplates" :key="itemTemplate.id">
        <ChecklistTemplateItemEditable
          :item-template="itemTemplate"
          :is-deleting="checklistItemTemplatesStore.isDeletingChecklistTemplateItem(itemTemplate.id)"
          :is-duplicating="checklistItemTemplatesStore.isDuplicatingChecklistTemplateItem(itemTemplate.id)"
          :is-saving="checklistItemTemplatesStore.isUpdatingChecklistTemplateItem(itemTemplate.id)"
          :is-selected="selectedChecklistItemTemplateIds.includes(Number(itemTemplate.id))"
          :is-sorting="isReorderingChecklistItemTemplates"
          :error-message="itemSaveErrorMessage(itemTemplate.id)"
          @save="saveChecklistItemTemplate"
          @duplicate="duplicateChecklistItemTemplate"
          @delete="deleteChecklistItemTemplate"
          @toggle-selected="toggleChecklistItemTemplateSelection"
        />
      </template>
    </VueDraggable>

    <ul v-else class="flex flex-col gap-3">
      <ChecklistTemplateItem v-for="itemTemplate in sortedChecklistItemTemplates" :key="itemTemplate.id" :item-template="itemTemplate" />
    </ul>
  </section>
</template>

<script setup>
import { computed, onMounted, ref, watch } from 'vue'
import { VueDraggable } from 'vue-draggable-plus'
import { Button } from '@/components/ui/button/index.js'
import ChecklistTemplateItem from './ChecklistTemplateItem.vue'
import ChecklistTemplateItemEditable from './ChecklistTemplateItemEditable.vue'
import { useChecklistItemTemplatesStore } from '../../stores/checklistItemTemplatesStore.js'

const props = defineProps({
  checklistTemplateId: {
    type: [Number, String],
    required: true,
  },
  isEditable: {
    type: Boolean,
    default: true,
  },
})

const checklistItemTemplatesStore = useChecklistItemTemplatesStore(`checklist_template_${props.checklistTemplateId}`)
const itemSaveErrorMessages = ref({})
const reorderErrorMessage = ref(null)
const isReorderingChecklistItemTemplates = ref(false)
const editableSortedChecklistItemTemplates = ref([])
const selectedChecklistItemTemplateIds = ref([])

const sortedChecklistItemTemplates = computed(() => {
  const items = checklistItemTemplatesStore.itemsForChecklistTemplate(props.checklistTemplateId)

  return [...items].sort((left, right) => {
    const leftOrder = Number(left.order ?? Number.MAX_SAFE_INTEGER)
    const rightOrder = Number(right.order ?? Number.MAX_SAFE_INTEGER)

    if (leftOrder !== rightOrder) {
      return leftOrder - rightOrder
    }

    return Number(left.id) - Number(right.id)
  })
})

const itemCountLabel = computed(() => {
  const count = sortedChecklistItemTemplates.value.length

  return `${count} checklist template item${count === 1 ? '' : 's'}`
})
const isBulkDeletingChecklistItemTemplates = computed(() => {
  return checklistItemTemplatesStore.isBulkDeletingChecklistTemplateItems(props.checklistTemplateId)
})
const isBulkActionBusy = computed(() => {
  return isReorderingChecklistItemTemplates.value || isBulkDeletingChecklistItemTemplates.value
})
const selectedItemCount = computed(() => selectedChecklistItemTemplateIds.value.length)
const selectedItemCountLabel = computed(() => {
  const count = selectedItemCount.value

  return count === 0
    ? 'No items selected'
    : `${count} item${count === 1 ? '' : 's'} selected`
})
const checklistItemTemplateIds = computed(() => {
  return sortedChecklistItemTemplates.value
    .map((itemTemplate) => Number(itemTemplate.id))
    .filter((checklistItemTemplateId) => checklistItemTemplateId > 0)
})
const areAllChecklistItemTemplatesSelected = computed(() => {
  return checklistItemTemplateIds.value.length > 0 && selectedItemCount.value === checklistItemTemplateIds.value.length
})
const canDeleteSelectedChecklistItemTemplates = computed(() => {
  return props.isEditable && selectedItemCount.value > 0 && !isBulkActionBusy.value
})

const errorMessage = computed(() => {
  if (!checklistItemTemplatesStore.error) {
    return null
  }

  return checklistItemTemplatesStore.error.response?.data?.message ?? checklistItemTemplatesStore.error.message ?? 'Unable to load checklist template items.'
})

const loadChecklistItemTemplates = async () => {
  await checklistItemTemplatesStore.fetchItemsForChecklistTemplateOnce(props.checklistTemplateId)
}

const synchronizeEditableChecklistItemTemplates = () => {
  editableSortedChecklistItemTemplates.value = [...sortedChecklistItemTemplates.value]
}

const itemSaveErrorMessage = (checklistItemTemplateId) => {
  return itemSaveErrorMessages.value[Number(checklistItemTemplateId)] ?? null
}

const clearItemSaveErrorMessage = (checklistItemTemplateId) => {
  const normalizedChecklistItemTemplateId = Number(checklistItemTemplateId)

  if (
    normalizedChecklistItemTemplateId < 1
    || !Object.prototype.hasOwnProperty.call(itemSaveErrorMessages.value, normalizedChecklistItemTemplateId)
  ) {
    return
  }

  const nextItemSaveErrorMessages = { ...itemSaveErrorMessages.value }
  delete nextItemSaveErrorMessages[normalizedChecklistItemTemplateId]
  itemSaveErrorMessages.value = nextItemSaveErrorMessages
}

const toggleChecklistItemTemplateSelection = (itemTemplate) => {
  if (!props.isEditable || isBulkActionBusy.value) {
    return
  }

  const checklistItemTemplateId = Number(itemTemplate?.id ?? 0)
  if (checklistItemTemplateId < 1) {
    return
  }

  if (selectedChecklistItemTemplateIds.value.includes(checklistItemTemplateId)) {
    selectedChecklistItemTemplateIds.value = selectedChecklistItemTemplateIds.value.filter((id) => id !== checklistItemTemplateId)

    return
  }

  selectedChecklistItemTemplateIds.value = [...selectedChecklistItemTemplateIds.value, checklistItemTemplateId]
}

const toggleSelectAllChecklistItemTemplates = () => {
  if (!props.isEditable || isBulkActionBusy.value) {
    return
  }

  if (areAllChecklistItemTemplatesSelected.value) {
    selectedChecklistItemTemplateIds.value = []

    return
  }

  selectedChecklistItemTemplateIds.value = [...checklistItemTemplateIds.value]
}

const saveChecklistItemTemplate = async ({ itemTemplate, instruction, details } = {}) => {
  if (!props.isEditable || !itemTemplate || isReorderingChecklistItemTemplates.value) {
    return
  }

  const checklistItemTemplateId = Number(itemTemplate.id ?? 0)
  if (checklistItemTemplateId < 1) {
    return
  }

  clearItemSaveErrorMessage(checklistItemTemplateId)

  try {
    await checklistItemTemplatesStore.updateInstruction(itemTemplate, instruction)
    const refreshedChecklistItemTemplate = checklistItemTemplatesStore.getItemById(checklistItemTemplateId)

    await checklistItemTemplatesStore.updateDetails(refreshedChecklistItemTemplate ?? itemTemplate, details)
  } catch (error) {
    itemSaveErrorMessages.value = {
      ...itemSaveErrorMessages.value,
      [checklistItemTemplateId]: error.response?.data?.message ?? error.message ?? 'Unable to save checklist template item.',
    }
  }
}

const duplicateChecklistItemTemplate = async (itemTemplate) => {
  if (!props.isEditable || !itemTemplate || isBulkActionBusy.value) {
    return
  }

  const checklistItemTemplateId = Number(itemTemplate.id ?? 0)
  if (checklistItemTemplateId < 1) {
    return
  }

  clearItemSaveErrorMessage(checklistItemTemplateId)

  try {
    await checklistItemTemplatesStore.duplicateItem(itemTemplate)
  } catch (error) {
    itemSaveErrorMessages.value = {
      ...itemSaveErrorMessages.value,
      [checklistItemTemplateId]: error.response?.data?.message ?? error.message ?? 'Unable to duplicate checklist template item.',
    }
  }
}

const deleteChecklistItemTemplate = async (itemTemplate) => {
  if (!props.isEditable || isBulkActionBusy.value) {
    return
  }

  if (!window.confirm('Delete this checklist template item?')) {
    return
  }

  clearItemSaveErrorMessage(itemTemplate.id)

  try {
    await checklistItemTemplatesStore.deleteItem(itemTemplate.id)
  } catch (error) {
    itemSaveErrorMessages.value = {
      ...itemSaveErrorMessages.value,
      [Number(itemTemplate.id)]: error.response?.data?.message ?? error.message ?? 'Unable to delete checklist template item.',
    }
  }
}

const deleteSelectedChecklistItemTemplates = async () => {
  if (!canDeleteSelectedChecklistItemTemplates.value) {
    return
  }

  const checklistItemTemplateIdsToDelete = [...selectedChecklistItemTemplateIds.value]

  if (!window.confirm(`Delete ${checklistItemTemplateIdsToDelete.length} selected checklist template item${checklistItemTemplateIdsToDelete.length === 1 ? '' : 's'}?`)) {
    return
  }

  reorderErrorMessage.value = null
  checklistItemTemplateIdsToDelete.forEach(clearItemSaveErrorMessage)

  try {
    await checklistItemTemplatesStore.bulkDeleteItemsForChecklistTemplate(props.checklistTemplateId, checklistItemTemplateIdsToDelete)
    selectedChecklistItemTemplateIds.value = []
  } catch (error) {
    reorderErrorMessage.value = error.response?.data?.message ?? error.message ?? 'Unable to delete the selected checklist template items.'
  }
}

const handleChecklistItemTemplateSortEnd = async (event) => {
  if (!props.isEditable || isReorderingChecklistItemTemplates.value) {
    return
  }

  const oldIndex = Number(event?.oldIndex ?? -1)
  const newIndex = Number(event?.newIndex ?? -1)
  if (oldIndex < 0 || newIndex < 0 || oldIndex === newIndex) {
    synchronizeEditableChecklistItemTemplates()
    return
  }

  reorderErrorMessage.value = null
  isReorderingChecklistItemTemplates.value = true

  try {
    const reorderedChecklistItemTemplates = [...editableSortedChecklistItemTemplates.value]

    for (const [nextOrder, checklistItemTemplate] of reorderedChecklistItemTemplates.entries()) {
      if (Number(checklistItemTemplate.order ?? -1) === nextOrder) {
        continue
      }

      clearItemSaveErrorMessage(checklistItemTemplate.id)

      await checklistItemTemplatesStore.updateItem(checklistItemTemplate, {
        order: nextOrder,
      })
    }
  } catch (error) {
    reorderErrorMessage.value = error.response?.data?.message ?? error.message ?? 'Unable to reorder checklist template items.'
  } finally {
    isReorderingChecklistItemTemplates.value = false

    try {
      await checklistItemTemplatesStore.fetchItemsForChecklistTemplate(props.checklistTemplateId)
    } catch (error) {
      reorderErrorMessage.value = reorderErrorMessage.value
        ?? error.response?.data?.message
        ?? error.message
        ?? 'Unable to refresh checklist template items after reordering.'
    }

    synchronizeEditableChecklistItemTemplates()
  }
}

watch(() => props.checklistTemplateId, async () => {
  itemSaveErrorMessages.value = {}
  reorderErrorMessage.value = null
  editableSortedChecklistItemTemplates.value = []
  selectedChecklistItemTemplateIds.value = []
  await loadChecklistItemTemplates()
})

watch(sortedChecklistItemTemplates, (checklistItemTemplates) => {
  const availableChecklistItemTemplateIds = new Set(
    checklistItemTemplates
      .map((itemTemplate) => Number(itemTemplate.id))
      .filter((checklistItemTemplateId) => checklistItemTemplateId > 0)
  )

  selectedChecklistItemTemplateIds.value = selectedChecklistItemTemplateIds.value.filter((checklistItemTemplateId) => {
    return availableChecklistItemTemplateIds.has(checklistItemTemplateId)
  })

  if (isReorderingChecklistItemTemplates.value) {
    return
  }

  synchronizeEditableChecklistItemTemplates()
}, { immediate: true, deep: true })

onMounted(async () => {
  await loadChecklistItemTemplates()
})
</script>
